import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, LOGO_LIGHT, LOGO_DARK } from "@/lib/scroll";

const LINKS = [
  { label: "Metodo", id: "metodo" },
  { label: "Servizi", id: "servizi" },
  { label: "Risultati", id: "risultati" },
  { label: "Contatti", id: "contatti" },
];

const Logo = ({ light }) => (
  <button
    data-testid="nav-logo"
    onClick={() => scrollToId("hero")}
    className="flex items-center"
    aria-label="Alba Selection — torna all'inizio"
  >
    <img
      src={light ? LOGO_LIGHT : LOGO_DARK}
      alt="Alba Selection"
      className="h-11 w-auto md:h-14"
    />
  </button>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "border-b border-navy/10 bg-cream/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <Logo light={!scrolled} />

          <div className="hidden items-center gap-9 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`group relative font-display text-sm font-medium tracking-wide transition-colors duration-300 ${
                  scrolled ? "text-navy hover:text-gold" : "text-white/85 hover:text-white"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            <button
              data-testid="nav-cta"
              onClick={() => go("contatti")}
              className="rounded-full bg-gold px-5 py-2.5 font-display text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-amber hover:text-white"
            >
              Analisi gratuita
            </button>
          </div>

          <button
            data-testid="nav-hamburger"
            onClick={() => setOpen(true)}
            className={`md:hidden ${scrolled ? "text-navy" : "text-white"}`}
            aria-label="Apri menu"
          >
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-navy-deep/98 backdrop-blur-lg md:hidden"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <Logo light />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-white"
                aria-label="Chiudi menu"
              >
                <X className="h-7 w-7" />
              </button>
            </div>
            <div className="flex flex-col gap-2 px-6 pt-10">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                  onClick={() => go(l.id)}
                  className="border-b border-white/10 py-4 text-left font-display text-2xl font-light text-white"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                data-testid="mobile-cta"
                onClick={() => go("contatti")}
                className="mt-8 rounded-full bg-gold px-6 py-4 font-display text-base font-semibold text-navy-deep"
              >
                Richiedi l'analisi gratuita
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
